// 設定 › 本地 VMS（SPEC #vms-web-offline「登記這台電腦」步驟 1、「撤銷」、元件表 VMS Web 列；app/vms#11）。
// 上：可登記的 Station（場域沒開斷網可用的說明怎麼開）、〔在這台電腦開啟本地 VMS〕→ 建登記請求、開新分頁到 Station 的 /enroll、
// 這一頁轉到確認畫面（LocalVmsApprove）輪詢。中：我登記的電腦（一台電腦一列：主網址 + 備援；最後更新、到期、撤銷、
// 備援網址需要更新、PIN 已鎖 /〔重設 PIN〕）。下：家戶 owner / admin 才看得到「這個家戶的其他登記」（管理員撤銷）。
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { useScope } from '../../lib/scope';
import { useApp } from '../../lib/AppContext';
import { useConfirm } from '../../components/ConfirmDialog';
import { Dialog } from '../../components/Dialog';
import { useHomeDetails } from '../devices/useHomeDetails';
import { enrollUrl, expiryState, groupComputers, localVmsApi, stationState, validPin, type LocalVmsComputer, type LocalVmsStation } from '../../lib/localVms';
import { stamp } from '../../lib/timefmt';
import { useHomePath } from '../devices/path';

type WithApi<T> = T & { api: string };
type T = (k: string, o?: Record<string, unknown>) => string;

/** 錯誤碼 → 文案（localvms.err_<code>，沒有就通用句）；LOCAL_VMS_LIMIT 帶 detail.limit。 */
export function lvErr(t: T, e: unknown): string {
  const code = isKinioError(e) ? e.code : 'default';
  const detail = (isKinioError(e) ? (e.body?.detail as Record<string, unknown> | undefined) : undefined) ?? {};
  return t(`localvms.err_${code}`, { limit: detail.limit ?? '—', defaultValue: t('localvms.err_default', { code }) });
}

export function fmtShort(iso: string, _lang: string): string {
  return stamp(iso);
}

/** 〔在這台電腦開啟本地 VMS〕：先同步開分頁（不被擋）、建請求、把分頁導到 Station 的 /enroll，再到確認畫面輪詢。 */
export function useStartEnroll() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  async function start(api: string, st: Pick<LocalVmsStation, 'device_ref' | 'display_name' | 'main_url'>) {
    if (!st.main_url) return;
    setErr(null);
    setBusy(st.device_ref);
    const w = window.open('', '_blank');
    try {
      const r = await localVmsApi.createRequest(api, st.device_ref);
      const url = enrollUrl(st.main_url, r.nonce, r.enroll_url);
      if (w) {
        w.opener = null;
        w.location.href = url;
      }
      navigate('/settings/local-vms/approve?req=' + encodeURIComponent(r.request_id), {
        state: { api, station_name: st.display_name, device_ref: st.device_ref, enroll_url: url, blocked: !w },
      });
    } catch (e) {
      w?.close();
      setErr(lvErr(t, e));
    } finally {
      setBusy(null);
    }
  }
  return { start, busy, err };
}

function ExpiryCell({ c }: { c: LocalVmsComputer }) {
  const { t, i18n } = useTranslation();
  const ex = expiryState(c);
  return (
    <td className="small">
      {fmtShort(c.expires_at, i18n.language)}{' '}
      {ex.kind === 'expired' ? (
        <span className="badge badge-err">{t('localvms.expired')}</span>
      ) : ex.kind === 'soon' ? (
        <span className="badge badge-warn">{t('localvms.expires_in', { n: ex.days })}</span>
      ) : (
        <span className="badge">{t('localvms.days_left', { n: ex.days })}</span>
      )}
    </td>
  );
}

function ComputerName({ c }: { c: LocalVmsComputer }) {
  const { t } = useTranslation();
  return (
    <>
      {c.label}
      {c.has_fallback && <span className="muted small"> · {t('localvms.with_fallback')}</span>}
      {c.fallback_needs_update && <span className="badge badge-warn"> {t('localvms.fallback_update')}</span>}
    </>
  );
}

export function LocalVmsSection() {
  const { t, i18n } = useTranslation();
  const { planes, visible } = useScope();
  const homePath = useHomePath();
  const me = useApp().session?.user?.user_id ?? '';
  const qc = useQueryClient();
  const { ask, dialog } = useConfirm();
  const { start, busy, err: startErr } = useStartEnroll();
  const [err, setErr] = useState<string | null>(null);
  const [pinFor, setPinFor] = useState<WithApi<LocalVmsComputer> | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const sq = useQueries({ queries: planes.map((p) => ({ queryKey: ['local-vms-stations', p.api], queryFn: () => localVmsApi.stations(p.api), retry: false })) });
  const eq = useQueries({ queries: planes.map((p) => ({ queryKey: ['local-vms-enrollments', p.api], queryFn: () => localVmsApi.enrollments(p.api), retry: false })) });
  const notYet = sq.length > 0 && sq.every((q) => q.isError && isKinioError(q.error) && (q.error.status === 404 || q.error.status === 405));
  const loading = sq.some((q) => q.isLoading) || eq.some((q) => q.isLoading);
  const failed = !notYet && [...sq, ...eq].some((q) => q.isError);
  const stations: WithApi<LocalVmsStation>[] = sq.flatMap((q, i) => (q.data ?? []).map((s) => ({ ...s, api: planes[i].api })));
  const computers: WithApi<LocalVmsComputer>[] = eq.flatMap((q, i) => groupComputers(q.data ?? []).map((c) => ({ ...c, api: planes[i].api })));
  const stationOf = (c: WithApi<LocalVmsComputer>) => stations.find((s) => s.api === c.api && s.device_ref === c.device_ref);
  const stationName = (c: WithApi<LocalVmsComputer>) => stationOf(c)?.display_name || c.device_ref;

  // 家戶 owner / admin：家戶裡別人的登記（有 Station 的家戶才查）
  const adminHomes = visible.filter((h) => h.can_manage && h.endpoints?.api && stations.some((s) => s.home_id === h.home_id && s.api === h.endpoints!.api));
  const hq = useQueries({
    queries: adminHomes.map((h) => ({ queryKey: ['local-vms-home', h.endpoints!.api, h.home_id], queryFn: () => localVmsApi.homeEnrollments(h.endpoints!.api, h.home_id), retry: false })),
  });
  const details = useHomeDetails();
  const memberName = (homeId: string, userId: string) => {
    const m = (details.find((d) => d.home.home_id === homeId)?.detail?.members ?? []).find((x) => (x as { user_id: string }).user_id === userId) as
      | { display_name?: string; email?: string }
      | undefined;
    return m?.display_name || m?.email || t('iam.left_member');
  };
  const others: WithApi<LocalVmsComputer>[] = hq.flatMap((q, i) =>
    groupComputers(q.data ?? [])
      .filter((c) => c.user_id !== me)
      .map((c) => ({ ...c, api: adminHomes[i].endpoints!.api })),
  );

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['local-vms-enrollments'] });
    void qc.invalidateQueries({ queryKey: ['local-vms-home'] });
  };

  async function revoke(c: WithApi<LocalVmsComputer>, admin: boolean) {
    const ok = await ask({ danger: true, title: t('localvms.revoke_confirm', { label: c.label }), body: t('localvms.revoke_body', { station: stationName(c) }), action: t('localvms.revoke') });
    if (!ok) return;
    try {
      if (admin) await localVmsApi.homeRevoke(c.api, c.home_id, c.enr_id);
      else await localVmsApi.revoke(c.api, c.enr_id);
      setErr(null);
      setNotice(t('localvms.revoked', { label: c.label }));
      refresh();
    } catch (x) {
      setErr(lvErr(t, x));
    }
  }

  return (
    <>
      <section className="card" data-testid="local-vms-stations" aria-labelledby="lv-title">
        <h2 className="section-title" id="lv-title">
          {t('localvms.title')}
        </h2>
        <p className="muted small">{t('localvms.intro')}</p>
        {notYet && <p className="muted small">{t('org.err_FEATURE_DISABLED')}</p>}
        {failed && <p className="error small">{t('scope.load_failed')}</p>}
        {(startErr || err) && (
          <p className="error small" role="alert">
            {startErr || err}
          </p>
        )}
        {loading && <p className="muted">{t('common.loading')}</p>}
        {!loading && !notYet && !failed && stations.length === 0 && <p className="muted small">{t('localvms.no_stations')}</p>}
        {/* 沒開「斷網可用」的場域收成一條橫幅說一次（第六輪 R6-P2-4：原本每列重複同一句 9 次）；表格那一欄只寫「沒開」 */}
        {stations.some((s) => stationState(s) === 'not_allowed') && (
          <p className="notice small" data-testid="localvms-not-allowed-banner">
            {t('localvms.not_allowed_banner', { count: stations.filter((s) => stationState(s) === 'not_allowed').length })}
          </p>
        )}
        {stations.length > 0 && (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>{t('localvms.col_station')}</th>
                  <th>{t('localvms.col_home')}</th>
                  <th>{t('localvms.col_offline')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {stations.map((s) => {
                  const st = stationState(s);
                  return (
                    <tr key={s.api + s.device_ref} data-station={s.device_ref}>
                      <td>{s.display_name}</td>
                      <td>{homePath(s.home_id, s.home_name)}</td>
                      <td className="small">
                        {st === 'not_allowed' ? (
                          <span className="muted">{t('localvms.not_allowed_short')}</span>
                        ) : (
                          <span className="badge">{s.offline_max_hours ? t('localvms.max_hours', { n: s.offline_max_hours }) : t('localvms.allowed')}</span>
                        )}
                      </td>
                      <td>
                        {st === 'ready' && (
                          <button className="btn btn-primary btn-sm" disabled={busy !== null} onClick={() => void start(s.api, s)}>
                            {t('localvms.open_here')}
                          </button>
                        )}
                        {st === 'not_ready' && <span className="muted small">{t('localvms.not_ready')}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {stations.some((s) => s.offline_allowed) && <p className="muted small">{t('localvms.fixed_ip_hint')}</p>}
      </section>

      <section className="card" data-testid="local-vms-enrollments" aria-labelledby="lv-enr-title">
        <h2 className="section-title" id="lv-enr-title">
          {t('localvms.enrolled_title')}
        </h2>
        {notice && (
          <p className="small" role="status">
            {notice}
          </p>
        )}
        {!loading && !notYet && computers.length === 0 && <p className="muted small">{t('localvms.enrolled_empty')}</p>}
        {computers.length > 0 && (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>{t('localvms.col_computer')}</th>
                  <th>{t('localvms.col_station')}</th>
                  <th>{t('localvms.col_updated')}</th>
                  <th>{t('localvms.col_expires')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {computers.map((c) => {
                  const ex = expiryState(c);
                  const st = stationOf(c);
                  const canRedo = !!st && stationState(st) === 'ready';
                  const redo = ex.kind !== 'ok' || c.fallback_needs_update;
                  return (
                    <tr key={c.api + c.id} data-computer={c.id}>
                      <td>
                        <ComputerName c={c} />
                      </td>
                      <td>{stationName(c)}</td>
                      <td className="small">{fmtShort(c.updated_at, i18n.language)}</td>
                      <ExpiryCell c={c} />
                      <td>
                        <div className="row-end" style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                          {c.pin_locked && (
                            <span className="badge badge-err">
                              {c.locked_until ? t('localvms.pin_locked', { until: fmtShort(c.locked_until, i18n.language) }) : t('localvms.pin_locked_plain')}
                            </span>
                          )}
                          {c.pin_locked && ex.kind !== 'expired' && (
                            <button className="btn btn-primary btn-sm" onClick={() => (setNotice(null), setPinFor(c))}>
                              {t('localvms.reset_pin')}
                            </button>
                          )}
                          {redo && (
                            <button className="btn btn-sm" disabled={!canRedo || busy !== null} title={canRedo ? undefined : t('localvms.redo_unavailable')} onClick={() => st && void start(c.api, st)}>
                              {t('localvms.redo')}
                            </button>
                          )}
                          <button className="btn btn-sm" onClick={() => void revoke(c, false)}>
                            {t('localvms.revoke')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted small">{t('localvms.pin_lock_hint')}</p>
      </section>

      {others.length > 0 && (
        <section className="card" data-testid="local-vms-home-enrollments" aria-labelledby="lv-home-title">
          <h2 className="section-title" id="lv-home-title">
            {t('localvms.home_title')}
          </h2>
          <p className="muted small">{t('localvms.home_hint')}</p>
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>{t('localvms.col_person')}</th>
                  <th>{t('localvms.col_computer')}</th>
                  <th>{t('localvms.col_station')}</th>
                  <th>{t('localvms.col_updated')}</th>
                  <th>{t('localvms.col_expires')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {others.map((c) => (
                  <tr key={c.api + c.id} data-computer={c.id}>
                    <td>{memberName(c.home_id, c.user_id)}</td>
                    <td>
                      <ComputerName c={c} />
                    </td>
                    <td>{stationName(c)}</td>
                    <td className="small">{fmtShort(c.updated_at, i18n.language)}</td>
                    <ExpiryCell c={c} />
                    <td>
                      <button className="btn btn-sm" onClick={() => void revoke(c, true)}>
                        {t('localvms.revoke')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {pinFor && (
        <ResetPinDialog
          c={pinFor}
          onClose={(done) => {
            setPinFor(null);
            if (done) {
              setNotice(t('localvms.pin_reset_done', { label: pinFor.label }));
              refresh();
            }
          }}
        />
      )}
      {dialog}
    </>
  );
}

function ResetPinDialog({ c, onClose }: { c: WithApi<LocalVmsComputer>; onClose(done: boolean): void }) {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit(ev: FormEvent) {
    ev.preventDefault();
    if (!validPin(pin)) return setErr(t('localvms.pin_invalid'));
    setBusy(true);
    setErr(null);
    try {
      await localVmsApi.resetPin(c.api, c.enr_id, pin);
      onClose(true);
    } catch (x) {
      setErr(lvErr(t, x));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open title={t('localvms.reset_pin_title', { label: c.label })} onClose={() => onClose(false)}>
      <form className="form" onSubmit={submit} data-testid="local-vms-reset-pin">
        <p className="muted small">{t('localvms.reset_pin_body')}</p>
        <label className="field">
          <span>{t('localvms.pin_label')}</span>
          <input
            className="input mono"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={6}
            value={pin}
            onChange={(x) => setPin(x.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="••••••"
          />
        </label>
        <p className="muted small">{t('localvms.stepup_note')}</p>
        {err && (
          <div className="error" role="alert">
            {err}
          </div>
        )}
        <div className="row-end">
          <button type="button" className="btn" onClick={() => onClose(false)}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || !validPin(pin)}>
            {t('localvms.reset_pin_submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
